// src/components/AddTradingAccount.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { encrypt } from '../utils/encryption';
import { updateAccountStatus } from '../utils/firebaseHelpers';
import TermsModal from './TermsModal';

// Enum for verification status
enum VerificationStatus {
  IDLE = 'idle',
  CHECKING = 'checking',
  VERIFIED = 'verified',
  FAILED = 'failed'
}

// Enum for broker type
enum BrokerType {
  NONE = 'none',
  XM = 'xm',
  WELTRADE = 'weltrade'
}

// Interface for MetaAPI account data
interface MetaApiAccount {
  accountId: string;
  accountName: string;
  login: string;
  server: string;
  platform: string;
}

// Interface for deployment result
interface DeploymentResult {
  success: boolean;
  message: string;
  account?: MetaApiAccount;
  accountData?: Record<string, unknown>;
  metaApiAccountId?: string;
  savedByBackend: boolean;
}

// Interface for account data
interface AccountData {
  accountNumber: string;
  accountName: string;
  server: string;
  password: string;
  platform: string;
  brokerType: BrokerType;
  createdAt: Date;
  isConnected: boolean;
  metaApiAccountId?: string;
}

const AddTradingAccount: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  
  // State for broker selection
  const [selectedBroker, setSelectedBroker] = useState<BrokerType>(BrokerType.NONE);
  
  // State for account verification
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>(VerificationStatus.IDLE);
  const [verificationMessage, setVerificationMessage] = useState<string | React.ReactNode>('');
  
  // State for account details
  const [accountName, setAccountName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [serverName, setServerName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';
  
  // Add this state for affiliation help
  const [affiliationHelp, setAffiliationHelp] = useState<{show: boolean, message: string}>({show: false, message: ''});
  const [isDeploying, setIsDeploying] = useState<boolean>(false);
  const [deploymentError, setDeploymentError] = useState<string>('');

  // Add loading state
  const [isProvisioning, setIsProvisioning] = useState<boolean>(false);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Function to check account affiliation using Firebase HTTP Function
  const checkAffiliation = async () => {
    if (!accountNumber.trim()) {
      setVerificationMessage('Please enter an account number');
      return;
    }
    
    setVerificationStatus(VerificationStatus.CHECKING);
    setVerificationMessage('Checking affiliation...');
    // Clear any previous error messages
    setErrorMessage('');
    
    try {
      // Add console logs for debugging
      const url = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/checkAffiliation?accountNumber=${accountNumber}`;
      console.log('Making request to:', url);
      
      const response = await fetch(
        url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        console.error('API returned non-OK status:', response.status);
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (err) {
        console.error('Error parsing JSON response:', err);
        throw new Error('Invalid response format');
      }
      
      if (data.isAffiliated === true) {
        setVerificationStatus(VerificationStatus.VERIFIED);
        setVerificationMessage(
          <div className="space-y-2">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Account Successfully Verified!</span>
            </div>
            <div className="pl-7">
              <p>Your XM Global account is affiliated with T1 FX Team.</p>
              <p className="mt-1">
                <span className="font-medium">Account Number: </span>
                <span className="bg-green-50 px-2 py-0.5 rounded font-mono">{accountNumber}</span>
              </p>
              <p className="mt-2 text-sm">You can now proceed with connecting your account below.</p>
            </div>
          </div>
        );
      } else {
        setVerificationStatus(VerificationStatus.FAILED);
        setVerificationMessage(
          <div className="space-y-2">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Account Not Affiliated</span>
            </div>
            <div className="pl-7">
              <p>Account #{accountNumber} is not affiliated with T1 FX Team.</p>
              <p className="mt-2 text-sm text-red-700">{data.message || 'Please check the account number or follow the instructions below to create an affiliated account.'}</p>
            </div>
          </div>
        );
        setAffiliationHelp({
          show: true,
          message: `Your account is not affiliated with T1 FX Team. To create an affiliated account:

1. Visit XM Global's website using our affiliate link
2. Click on "Open Account" in the top right corner
3. Fill in your personal information
4. Click on "Have a Partner Code? Enter here" and enter our partner code: T1FXTEAM
5. Complete the registration process
6. Once your account is created, return here and enter your new account number

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
For Existing XM Global Account Holders
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

If you already have an XM Global account and want to affiliate it with T1 FX Team:
1. Log in to your existing XM Global profile at my.xm.com/member/login
2. Click on "Add New Account"
3. In the "Have a Partner Code?" field, enter our code: T1FXTEAM
4. Complete the account creation process
5. Return here and enter your new account number

This will ensure your new account is properly affiliated with T1 FX Team.`
        });
      }
    } catch (error) {
      console.error('Affiliation check error:', error);
      setVerificationStatus(VerificationStatus.FAILED);
      setVerificationMessage(
        <div className="space-y-2">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Server Error</span>
          </div>
          <div className="pl-7">
            <p>Unable to verify account affiliation at this time.</p>
            <p className="mt-2 text-sm text-red-700">Please try again later or contact support if the issue persists.</p>
          </div>
        </div>
      );
      setErrorMessage('Server error. Please try again later.');
    }
  };

  // Function to check if auto-deploy is enabled
  const checkAutoDeploy = async (): Promise<boolean> => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'metaApi'));
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        return settings.autoDeploy === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking auto-deploy setting:', error);
      return false;
    }
  };

  // Function to deploy account to MetaAPI
  const deployToMetaApi = async (accountData: AccountData): Promise<DeploymentResult> => {
    setIsProvisioning(true);
    try {
      console.log('Account Data received:', {
        ...accountData,
        accountNumber: accountData.accountNumber,
        password: '[REDACTED]'
      });

      if (!accountData.accountNumber) {
        console.error('Account number is missing in deployToMetaApi');
        throw new Error('Account number is required for MetaAPI deployment');
      }

      const url = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/provisionFullAccount`;
      
      const requestBody = {
        login: accountData.accountNumber,
        password: accountData.password,
        serverName: accountData.server,
        accountName: accountData.accountName,
        platform: 'mt5',
        userId: currentUser?.uid,
        brokerType: accountData.brokerType
      };

      console.log('Request body prepared:', {
        ...requestBody,
        login: requestBody.login,
        password: '[REDACTED]'
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (err) {
        console.error('Error parsing response:', err);
        throw new Error('Unable to process server response. Please try again or contact support.');
      }

      if (!response.ok || !data.success) {
        // Extract specific error details if available
        const errorDetails = data.error || data.message || '';
        let userFriendlyMessage = '';
        
        // Use the API's error message if available
        if (data.message) {
          userFriendlyMessage = data.message;
        } else {
          userFriendlyMessage = 'Failed to connect your trading account. ';
          
          // Add specific error context based on the error message
          if (errorDetails.toLowerCase().includes('invalid credentials')) {
            userFriendlyMessage += 'Please check your account number, password, and server details.';
          } else if (errorDetails.toLowerCase().includes('server')) {
            userFriendlyMessage += 'There was an issue connecting to the trading server. Please verify your server name.';
          } else if (errorDetails.toLowerCase().includes('timeout')) {
            userFriendlyMessage += 'The connection attempt timed out. Please try again.';
          } else {
            userFriendlyMessage += 'Please verify your account details and try again.';
          }
        }

        // Log the full error details for debugging
        console.error('API Error:', {
          status: response.status,
          error: data.error,
          message: data.message,
          details: errorDetails
        });
        
        throw new Error(userFriendlyMessage);
      }

      return {
        success: true,
        message: data.message || 'Account deployed successfully',
        account: data.account,
        accountData: data.accountData,
        metaApiAccountId: data.metaApiAccountId,
        savedByBackend: data.savedByBackend
      };
    } catch (error) {
      console.error('Error in deployToMetaApi:', error);
      throw error;
    } finally {
      setIsProvisioning(false);
    }
  };

  // Function to add trading account
  const addTradingAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Add validation for account number
    if (!accountNumber) {
      console.error('Account number is missing in form submission');
      setErrorMessage('Account number is required');
      return;
    }

    if (!accountName.trim() || !password.trim() || !serverName.trim()) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    if (!acceptedTerms) {
      setErrorMessage('You must accept the terms and conditions to proceed');
      return;
    }

    console.log('Starting account addition process with account number:', accountNumber);
    
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    setDeploymentError('');
    
    try {
      const shouldAutoDeploy = await checkAutoDeploy();
      console.log('Auto deploy enabled:', shouldAutoDeploy);
      
      if (shouldAutoDeploy) {
        setIsDeploying(true);
        try {
          // Create account data with explicit account number
          const accountData: AccountData = {
            accountNumber: accountNumber,
            accountName: accountName.trim(),
            server: serverName.trim(),
            password: password,
            platform: 'mt5',
            brokerType: selectedBroker,
            createdAt: new Date(),
            isConnected: false
          };

          console.log('Prepared account data for deployment:', {
            ...accountData,
            accountNumber: accountData.accountNumber,
            password: '[REDACTED]'
          });

          const deploymentResult = await deployToMetaApi(accountData);

          if (deploymentResult.success && currentUser && !deploymentResult.savedByBackend) {
            const accountRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountNumber);
            const firestoreData = {
              accountNumber: accountNumber,
              accountName: accountName.trim(),
              server: serverName.trim(),
              password: encrypt(password),
              platform: 'mt5',
              metaApiAccountId: deploymentResult.metaApiAccountId,
              isConnected: true,
              createdAt: serverTimestamp(),
              createdByAdmin: false,
              serverName: serverName.trim(),
              brokerType: selectedBroker
            };

            await setDoc(accountRef, firestoreData);
            
            await updateAccountStatus(
              currentUser.uid,
              'added_account',
              accountNumber
            );
            
            setSuccessMessage('Trading account added and deployed successfully!');
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else if (deploymentResult.success) {
            setSuccessMessage('Trading account added and deployed successfully!');
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          }
        } catch (error) {
          console.error('MetaAPI deployment error:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to deploy account to MetaAPI';
          setDeploymentError(errorMessage);
          setErrorMessage(errorMessage);
        } finally {
          setIsDeploying(false);
        }
      } else {
        // If auto-deploy is disabled, just save to database
        if (currentUser) {
          const accountRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountNumber);
          const accountData: AccountData = {
            accountNumber,
            accountName,
            server: serverName,
            password: encrypt(password), // Store encrypted password
            createdAt: new Date(),
            isConnected: false, // Set to false since we're not deploying
            platform: 'mt5',
            brokerType: selectedBroker
          };

          await setDoc(accountRef, accountData);
          
          // Update account status
          await updateAccountStatus(
            currentUser.uid,
            'added_account',
            accountNumber
          );
          
          setSuccessMessage('Trading account added successfully! You can deploy it later from the dashboard.');
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error adding trading account:', error);
      setErrorMessage('Failed to add trading account. Please try again or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fix verification status comparison
  const isVerificationPending = () => {
    return verificationStatus === VerificationStatus.IDLE || 
           verificationStatus === VerificationStatus.CHECKING || 
           verificationStatus === VerificationStatus.FAILED;
  };

  // Check for authenticated user
  if (!currentUser) {
    return <Navigate to="/" />;
  }

  // Check if user has an active subscription
  if (!userData?.subscription || userData.subscription.status !== 'active') {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation Header */}
      <nav className="bg-black shadow">
        {isStaging && (
          <div className="bg-amber-500 text-black text-center py-1 font-medium">
            ⚠️ STAGING ENVIRONMENT - FOR TESTING ONLY ⚠️
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img src="/T1Logo180.png" alt="T1 Logo" className="h-8 w-auto" />
                <div className="ml-3 flex items-center">
                  <span className="text-xl font-bold text-white">T1 FX TEAM</span>
                  {isStaging && (
                    <span className="ml-2 bg-amber-500 text-black text-xs px-2 py-0.5 rounded-full font-bold">
                      STAGING
                    </span>
                  )}
                </div>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link 
                  to="/dashboard" 
                  className="border-transparent text-gray-300 hover:border-gray-300 hover:text-white inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Dashboard
                </Link>
                <Link 
                  to="/add-trading-account" 
                  className="border-t1blue text-white inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                >
                  Add Trading Account
                </Link>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={handleSignOut}
                className="ml-4 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu - only shown on small screens */}
      <div className="sm:hidden bg-black border-t border-gray-700">
        <div className="px-2 py-3 space-y-1">
          <Link 
            to="/dashboard" 
            className="border-transparent text-gray-300 hover:bg-gray-900 hover:border-gray-300 hover:text-white block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
          >
            Dashboard
          </Link>
          <Link 
            to="/add-trading-account" 
            className="bg-gray-900 border-t1blue text-white block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
          >
            Add Trading Account
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Add Trading Account</h3>
              <p className="mt-1 text-sm text-gray-600">
                Connect your trading account to enable automated trading and analysis.
              </p>
            </div>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {/* Broker Selection */}
                {selectedBroker === BrokerType.NONE && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Select Your Broker</h4>
                    <p className="mb-4 text-sm text-gray-600">
                      Choose your broker to continue with account connection.
                    </p>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div 
                        onClick={() => setSelectedBroker(BrokerType.XM)}
                        className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-t1blue hover:bg-blue-50 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-t1blue cursor-pointer"
                      >
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-t1blue flex items-center justify-center text-white">
                            XM
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="focus:outline-none">
                            <span className="absolute inset-0" aria-hidden="true"></span>
                            <p className="text-sm font-medium text-gray-900">
                              XM Global
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              Requires affiliation verification
                            </p>
                          </div>
                        </div>
                      </div>

                      <div 
                        onClick={() => {
                          setSelectedBroker(BrokerType.WELTRADE);
                          // Skip affiliation check for Weltrade, set as verified
                          setVerificationStatus(VerificationStatus.VERIFIED);
                          // Clear any existing server options
                          setServerName('');
                          // Reset any error messages
                          setErrorMessage('');
                        }}
                        className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex items-center space-x-3 hover:border-t1blue hover:bg-blue-50 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-t1blue cursor-pointer"
                      >
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white">
                            W
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="focus:outline-none">
                            <span className="absolute inset-0" aria-hidden="true"></span>
                            <p className="text-sm font-medium text-gray-900">
                              Weltrade
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Back button - shown when broker is selected */}
                {selectedBroker !== BrokerType.NONE && (
                  <div className="flex justify-start mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBroker(BrokerType.NONE);
                        setVerificationStatus(VerificationStatus.IDLE);
                        setAccountNumber('');
                        setVerificationMessage('');
                        setErrorMessage('');
                        setServerName('');
                      }}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-1 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Change Broker
                    </button>
                  </div>
                )}

                {/* XM Account Verification - only shown for XM */}
                {selectedBroker === BrokerType.XM && isVerificationPending() && (
                  <div>
                    <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
                      Account Number
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        name="accountNumber"
                        id="accountNumber"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        className={`block w-full pr-10 focus:outline-none sm:text-sm rounded-md ${
                          verificationStatus === VerificationStatus.VERIFIED
                            ? 'border-green-300 text-green-900 placeholder-green-300 focus:ring-green-500 focus:border-green-500'
                            : verificationStatus === VerificationStatus.FAILED
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300 focus:ring-t1blue focus:border-t1blue'
                        }`}
                        placeholder="Enter your XM Global account number"
                        readOnly={verificationStatus === VerificationStatus.VERIFIED}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {verificationStatus === VerificationStatus.VERIFIED && (
                          <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={checkAffiliation}
                        disabled={!accountNumber.trim() || verificationStatus === VerificationStatus.CHECKING || verificationStatus === VerificationStatus.VERIFIED}
                        className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm ${
                          verificationStatus === VerificationStatus.VERIFIED
                            ? 'bg-green-600 text-white cursor-not-allowed'
                            : 'text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {verificationStatus === VerificationStatus.CHECKING ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Checking...
                          </>
                        ) : verificationStatus === VerificationStatus.VERIFIED ? (
                          <>
                            <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </>
                        ) : (
                          'Check Affiliation'
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Always show verification message if it exists */}
                {selectedBroker === BrokerType.XM && verificationMessage && (
                  <div className={`mt-4 rounded-lg p-4 ${
                    verificationStatus === VerificationStatus.VERIFIED
                      ? 'bg-green-50 border border-green-200'
                      : verificationStatus === VerificationStatus.FAILED
                      ? 'bg-red-50 border border-red-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}>
                    {verificationMessage}
                  </div>
                )}

                {/* Show affiliation help if needed */}
                {selectedBroker === BrokerType.XM && affiliationHelp.show && verificationStatus === VerificationStatus.FAILED && (
                  <AffiliationHelp 
                    message={affiliationHelp.message} 
                    onClose={() => setAffiliationHelp({ show: false, message: '' })} 
                  />
                )}

                {/* Weltrade Account Number Input - only shown for Weltrade */}
                {selectedBroker === BrokerType.WELTRADE && (
                  <div>
                    <label htmlFor="account-number" className="block text-sm font-medium text-gray-700">
                      Weltrade Account Number
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="account-number"
                        id="account-number"
                        className="focus:ring-t1blue focus:border-t1blue block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        placeholder="Enter your Weltrade account number"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      You can find your Weltrade account number in your MT5 platform or Weltrade client portal
                    </p>
                  </div>
                )}

                {/* Account Details Form - shown after verification for XM or immediately for Weltrade */}
                {(selectedBroker === BrokerType.WELTRADE || verificationStatus === VerificationStatus.VERIFIED) && (
                  <form onSubmit={addTradingAccount}>
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      
                      <h4 className="text-md font-medium text-gray-900 mb-4">
                        Connect {selectedBroker === BrokerType.XM ? 'XM Global' : 'Weltrade'} Account
                      </h4>
                      
                      <div className="mb-4">
                        <label htmlFor="account-name" className="block text-sm font-medium text-gray-700">
                          Account Display Name
                        </label>
                        <input
                          type="text"
                          name="account-name"
                          id="account-name"
                          className="mt-1 focus:ring-t1blue focus:border-t1blue block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder={`E.g., My ${selectedBroker === BrokerType.XM ? 'XM' : 'Weltrade'} Forex Account`}
                          value={accountName}
                          onChange={(e) => setAccountName(e.target.value)}
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">This is how the account will appear in your dashboard</p>
                      </div>
                      
                      <div className="mb-4">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          {selectedBroker === BrokerType.XM ? 'XM Global' : 'Weltrade'} Password
                        </label>
                        <div className="mt-1 relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            id="password"
                            className="focus:ring-t1blue focus:border-t1blue block w-full shadow-sm sm:text-sm border-gray-300 rounded-md pr-10"
                            placeholder={`Enter your ${selectedBroker === BrokerType.XM ? 'XM Global' : 'Weltrade'} password`}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 focus:outline-none"
                          >
                            {showPassword ? (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="server-name" className="block text-sm font-medium text-gray-700">
                          {selectedBroker === BrokerType.XM ? 'XM Global' : 'Weltrade'} Server
                        </label>
                        <input
                          type="text"
                          name="server-name"
                          id="server-name"
                          className="mt-1 focus:ring-t1blue focus:border-t1blue block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder={selectedBroker === BrokerType.XM ? 
                            `Enter your XM Global server name` : 
                            `Example: Weltrade-Real`}
                          value={serverName}
                          onChange={(e) => setServerName(e.target.value)}
                          required
                        />
                      </div>

                      {/* Add Terms and Conditions checkbox */}
                      <div className="mb-4">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="terms"
                              name="terms"
                              type="checkbox"
                              checked={acceptedTerms}
                              onChange={(e) => setAcceptedTerms(e.target.checked)}
                              className="h-4 w-4 text-t1blue focus:ring-t1blue border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="terms" className="font-medium text-gray-700">
                              I accept the{' '}
                              <button
                                type="button"
                                onClick={() => setIsTermsModalOpen(true)}
                                className="text-t1blue hover:text-t1blue-dark focus:outline-none"
                              >
                                Terms and Conditions
                              </button>
                            </label>
                            <p className="text-gray-500">
                              By accepting, you acknowledge that you have read and understood our terms and conditions, including the risks involved in automated trading.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Error Message Display - Add this right after the form fields */}
                      {(errorMessage || deploymentError) && (
                        <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">Connection Error</h3>
                              <div className="mt-2 text-sm text-red-700">
                                {errorMessage || deploymentError}
                              </div>
                              <div className="mt-2 text-xs text-red-600">
                                If this issue persists, please contact support.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Success Message Display */}
                      {successMessage && (
                        <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-md">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm text-green-700">
                                {successMessage}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Loading State Display */}
                      {isDeploying && (
                        <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-md">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm text-blue-700">
                                Connecting your trading account... Please wait.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {!successMessage && (
                        <div className="pt-4">
                          <button
                            type="submit"
                            disabled={isSubmitting || !acceptedTerms}
                            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue disabled:bg-t1blue-light/50"
                          >
                            {isSubmitting ? 'Adding Account...' : 'Add Account'}
                          </button>
                        </div>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="hidden sm:block" aria-hidden="true">
          <div className="py-5">
            <div className="border-t border-gray-200"></div>
          </div>
        </div>

        <div className="mt-10 sm:mt-0">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <div className="px-4 sm:px-0">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Need Help?</h3>
                <p className="mt-1 text-sm text-gray-600">
                  If you're having trouble connecting your trading account, check out our guides or contact support.
                </p>
              </div>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="shadow sm:rounded-md sm:overflow-hidden">
                <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                  <div className="grid grid-cols-1 gap-4">
                    {/* Show XM-specific help when XM broker is selected */}
                    {(selectedBroker === BrokerType.XM || selectedBroker === BrokerType.NONE) && (
                      <>
                        <div className="p-4 border border-gray-200 rounded-md">
                          <h4 className="text-sm font-medium text-gray-900">How to find your XM Global account number?</h4>
                          <p className="mt-1 text-sm text-gray-500">
                            Your XM Global account number can be found in your MT5 platform under Account Information or in your XM Global client area.
                          </p>
                        </div>
                        
                        <div className="p-4 border border-gray-200 rounded-md">
                          <h4 className="text-sm font-medium text-gray-900">Where to find your XM Global server?</h4>
                          <p className="mt-1 text-sm text-gray-500">
                            Your server information can be found in the MT5 login details provided by XM Global. Look for:
                          </p>
                          <ul className="mt-2 text-sm text-gray-500 list-disc pl-5 space-y-1">
                            <li>The welcome email from XM Global when you opened your account</li>
                            <li>The MT5 platform login screen - check the "Server" dropdown</li>
                            <li>Your XM Global client portal under "Account Information" section</li>
                          </ul>
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-800">
                            <strong>Important:</strong> The server name is case-sensitive and must be entered exactly as shown (e.g., "XMGlobal-MT5 4").
                          </div>
                        </div>
                        
                        <div className="p-4 border border-gray-200 rounded-md">
                          <h4 className="text-sm font-medium text-gray-900">How to create an affiliated account with T1 FX Team?</h4>
                          <div className="mt-2 space-y-4">
                            <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                              <h5 className="text-xs font-medium text-blue-800 mb-1">For New XM Global Users</h5>
                              <ol className="text-xs text-blue-700 space-y-1 list-decimal pl-4">
                                <li>Visit XM Global's website using our affiliate link</li>
                                <li>Click on "Open Account" in the top right corner</li>
                                <li>Fill in your personal information</li>
                                <li>Click on "Have a Partner Code? Enter here" and enter our partner code: <span className="font-semibold">T1FXTEAM</span></li>
                                <li>Complete the registration process</li>
                              </ol>
                            </div>
                            
                            <div className="bg-green-50 p-3 rounded-md border border-green-100">
                              <h5 className="text-xs font-medium text-green-800 mb-1">For Existing XM Global Account Holders</h5>
                              <ol className="text-xs text-green-700 space-y-1 list-decimal pl-4">
                                <li>Log in to your existing XM Global profile at my.xm.com/member/login</li>
                                <li>Click on "Add New Account"</li>
                                <li>In the "Have a Partner Code?" field, enter our code: <span className="font-semibold">T1FXTEAM</span></li>
                                <li>Complete the account creation process</li>
                              </ol>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                              <a
                                href="https://clicks.pipaffiliates.com/c?c=862658&l=en&p=1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
                              >
                                New to XM? Open an account
                              </a>
                              <a
                                href="https://my.xm.com/member/login"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
                              >
                                Already have an account? Log in
                              </a>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Show Weltrade-specific help when Weltrade broker is selected */}
                    {(selectedBroker === BrokerType.WELTRADE || selectedBroker === BrokerType.NONE) && (
                      <>
                        <div className="p-4 border border-gray-200 rounded-md">
                          <h4 className="text-sm font-medium text-gray-900">How to find your Weltrade account number?</h4>
                          <p className="mt-1 text-sm text-gray-500">
                            Your Weltrade account number can be found in your MT5 platform under Account Information or in your Weltrade dashboard.
                          </p>
                          <ul className="mt-2 text-sm text-gray-500 list-disc pl-5 space-y-1">
                            <li>Log in to your Weltrade dashboard</li>
                            <li>Navigate to MT5 accounts section</li>
                            <li>The account number will be displayed next to each MT5 account</li>
                          </ul>
                        </div>
                        
                        <div className="p-4 border border-gray-200 rounded-md">
                          <h4 className="text-sm font-medium text-gray-900">Where to find your Weltrade server?</h4>
                          <p className="mt-1 text-sm text-gray-500">
                            Your server information can be found in the MT5 login details provided by Weltrade. Look for:
                          </p>
                          <ul className="mt-2 text-sm text-gray-500 list-disc pl-5 space-y-1">
                            <li>The MT5 account setup email from Weltrade</li>
                            <li>The MT5 platform login screen - check the "Server" dropdown</li>
                            <li>Your Weltrade dashboard under MT5 Accounts section</li>
                          </ul>
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-800">
                            <strong>Important:</strong> The server name is case-sensitive and must be entered exactly as shown.
                          </div>
                        </div>
                        
                        <div className="p-4 border border-gray-200 rounded-md">
                          <h4 className="text-sm font-medium text-gray-900">How to create a Weltrade account?</h4>
                          <div className="mt-2 space-y-4">
                            <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                              <h5 className="text-xs font-medium text-blue-800 mb-1">For New Weltrade Users</h5>
                              <ol className="text-xs text-blue-700 space-y-1 list-decimal pl-4">
                                <li>Visit Weltrade's website using our partner link</li>
                                <li>Click on "Open Account" and fill in your personal information</li>
                                <li>Enter our referral code: CX40955</li>
                                <li>Verify your email and complete the registration process</li>
                                <li>Log in to your account and create a new MT5 account</li>
                              </ol>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                              <a
                                href="https://track.gowt.me/visit/?bta=40955&brand=weltrade"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Create a Weltrade account
                              </a>
                              <a
                                href="https://my.weltrade.com/login"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Log in to Weltrade dashboard
                              </a>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div className="p-4 border border-gray-200 rounded-md">
                      <h4 className="text-sm font-medium text-gray-900">Contact Support</h4>
                      <p className="mt-1 text-sm text-gray-500">
                        Still need help? Contact our support team at <a href="mailto:support@t1fxteam.co.za" className="text-t1blue hover:text-t1blue-light">support@t1fxteam.co.za</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </div>
  );
};

// Update the AffiliationHelp component to have a more modern card design
const AffiliationHelp: React.FC<{message: string, onClose: () => void}> = ({message, onClose}) => {
  return (
    <div className="mt-4 rounded-lg bg-white p-5 border border-gray-200 shadow-sm">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-800 mb-3">How to Create an Affiliated Account</h3>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
              <h4 className="text-sm font-medium text-blue-800 mb-2">For New XM Global Users</h4>
              <ol className="text-sm text-blue-700 space-y-1.5 list-decimal pl-5">
                <li>Visit XM Global's website using our affiliate link</li>
                <li>Click on "Open Account" in the top right corner</li>
                <li>Fill in your personal information</li>
                <li>Click on "Have a Partner Code? Enter here" and enter our partner code: <span className="font-semibold">T1FXTEAM</span></li>
                <li>Complete the registration process</li>
                <li>Once your account is created, return here and enter your new account number</li>
              </ol>
            </div>
            
            <div className="bg-green-50 p-4 rounded-md border border-green-100">
              <h4 className="text-sm font-medium text-green-800 mb-2">For Existing XM Global Account Holders</h4>
              <ol className="text-sm text-green-700 space-y-1.5 list-decimal pl-5">
                <li>Log in to your existing XM Global profile at my.xm.com/member/login</li>
                <li>Click on "Add New Account"</li>
                <li>In the "Have a Partner Code?" field, enter our code: <span className="font-semibold">T1FXTEAM</span></li>
                <li>Complete the account creation process</li>
                <li>Return here and enter your new account number</li>
              </ol>
              <p className="mt-2 text-sm text-green-700">This will ensure your new account is properly affiliated with T1 FX Team.</p>
            </div>
          </div>
          
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <a
              href="https://clicks.pipaffiliates.com/c?c=862658&l=en&p=1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-t1blue hover:bg-t1blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
            >
              New to XM? Open an account
            </a>
            <a
              href="https://my.xm.com/member/login"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-t1blue"
            >
              Already have an account? Log in
            </a>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="ml-4 text-gray-400 hover:text-gray-500 focus:outline-none"
        >
          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default AddTradingAccount;